import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  ControlledDateSelect,
  ControlledInput,
  ControlledSelect,
} from '@uikit/FormItems/ControlledItems';
import Modal from '@uikit/Modal/Modal';
import { getDirtyObjects, getDirtyValues } from '@src/utils';
import { validations } from '@utils/validations';
import React, { FC } from 'react';
import { useForm } from '@hooks/useDirtyForm';
import { useLazyGetGenderSelectDataQuery } from '@store/rtk/selects';
import { selectSelects } from '@store/slices/selects/selects';
import { selectMembersInfo } from '@store/slices/members/info/membersInfoSlice';
import {
  useCheckMemberMutation,
  useCreateOrUpdateMemberInfoMutation,
} from '@store/rtk/members';
import {
  maskedInputs,
  memberInfoFormType,
  memberInfoFormValues,
  objectFields,
  selectFields,
} from './constants';
import SubmitButton from '@uikit/FormItems/SubmitButton/SubmitButton';
import { useNavigate } from 'react-router-dom';
import { ControlledLocationInput } from '@uikit/FormItems/ControlledItems/ControlledLocationInput';
import { ControlledPhoneInput } from '@uikit/FormItems/ControlledItems/ControlledPhoneInput'; // import { ControlledPhoneInput } from '@uikit/FormItems/ControlledItems/ControlledPhoneInput';

// import { ControlledPhoneInput } from '@uikit/FormItems/ControlledItems/ControlledPhoneInput';

interface IMemberCreateModal {
  closeModal: () => void;
  setMemberStatus: (args: {
    status: 'CONFLICT' | 'NOT_FOUND' | 'FOUND' | null;
    id: number | null;
  }) => void;

  setToggleNotFoundModal: (a: boolean) => void;
  setMemberData: any;
}

const MemberCreateModal: FC<IMemberCreateModal> = ({
  closeModal,
  setToggleNotFoundModal,
  setMemberData,
  setMemberStatus,
}) => {
  const [getGenderSelectData] = useLazyGetGenderSelectDataQuery();
  const { genders } = useAppSelector(selectSelects);
  const dispatch = useAppDispatch();
  const [createOrUpdateMemberInfo] = useCreateOrUpdateMemberInfoMutation();
  const { isMemberSaved } = useAppSelector(selectMembersInfo);
  const navigate = useNavigate();

  const {
    control,
    watch,
    trigger,
    reset,
    setError,
    clearErrors,
    setValue,
    handleSubmit,
    formState: { defaultValues, dirtyFields, isSubmitting, isValid },
  } = useForm({ defaultValues: memberInfoFormValues, mode: 'all' });
  const [checkData] = useCheckMemberMutation();

  const updateMemberInfoAsync = async (values: memberInfoFormType) => {
    const dirtyValues = getDirtyValues({
      dirtyFields,
      values,
      maskedInputs,
      defaultValues,
      selectFields,
    });

    const dirtyObjects = getDirtyObjects({
      values,
      defaultValues,
      objectFields,
    });

    const allDirtyFields = {
      ...dirtyValues,
      ...dirtyObjects,
    };

    const newBody = {
      ...allDirtyFields,
      // dob: convertDateFormat(allDirtyFields?.dob as string),
    };

    const checkMemberData = await checkData({ body: newBody }).unwrap();

    closeModal?.();
    if (checkMemberData) {
      setMemberData(newBody);
      setMemberStatus({
        status: checkMemberData.status,
        id: checkMemberData?.data?.id,
      });
    }
  };

  const handleSetLocation = (e) => {
    const { value } = e.target;
    setValue('address', value, { shouldDirty: true });
  };

  return (
    <>
      <Modal
        title="Create Member"
        closeClick={closeModal}
        maxWidth={'608px'}
        dataTestId="mcm"
      >
        <div className="create-wrapper" data-test-id="mcm">
          <div className="member-create-description" data-test-id="mcm-desc">
            <p>
              To verify the member's identity and transfer their eligibility
              information to our system, please complete all required fields.
              This is required to access protected information electronically
              through the Clearinghouse.
            </p>
          </div>
          <div className="modal-member-create-form" data-test-id="mcm-form">
            <ControlledInput
              name="firstName"
              maxWidth="200px"
              trigger={trigger}
              title="Member Legal First Name"
              rules={{
                required: validations.required('Member Legal First Name'),
              }}
              control={control}
              dataTestId="mcm-fn"
            />
            <ControlledInput
              maxWidth="200px"
              name="lastName"
              trigger={trigger}
              rules={{
                required: validations.required('Member Legal Last Name'),
              }}
              title="Member Legal Last Name"
              control={control}
              dataTestId="mcm-ln"
            />
            <ControlledSelect
              control={control}
              selectOptions={genders}
              trigger={trigger}
              requestByAsync={() => getGenderSelectData({})}
              name="gender"
              title="Gender"
              rules={{
                validate: validations.selectRequired('Gender'),
              }}
              dataTestId="mcm-gender"
            />
            <ControlledInput
              control={control}
              maxWidth="200px"
              trigger={trigger}
              name="memberIdNumber"
              rules={{ required: validations.required('Member ID') }}
              title="Member ID"
              dataTestId="mcm-mid"
            />
            <ControlledDateSelect
              maxWidth="110px"
              control={control}
              trigger={trigger}
              maxDate={new Date()}
              name="dob"
              title="Date of Birth"
              rules={{ required: validations.required('Date of Birth') }}
              dataTestId="mcm-dob"
            />
            <div
              style={{
                display: 'flex',
                gap: 16,
                width: '100%',
                gridColumn: '1/-1',
              }}
              data-test-id="mcm-contact"
            >
              <div style={{ width: '240px' }}>
                <ControlledPhoneInput
                  mask="(999) 999-9999"
                  control={control}
                  name="phone"
                  title="Primary Contact Number"
                  required
                  setValue={setValue}
                  setError={setError}
                  clearErrors={clearErrors}
                  typeKey="primaryType"
                  rules={{
                    required: validations.required('Primary Contact Number'),
                    validate: {
                      exactLength: (value) =>
                        validations.length('Primary Contact Number', value, 10),
                    },
                  }}
                  dataTestId="mcm-phone"
                />
              </div>
              <ControlledLocationInput
                control={control}
                name="address"
                title="Address"
                rules={{
                  required: true,
                  validate: validations.required('Address is required.'),
                }}
                inputId="address"
                change={handleSetLocation}
                defaultValue=""
                dataTestId="mcm-addr"
              />
            </div>
          </div>

          <div className="member-create-submit-button" data-test-id="mcm-ft">
            <SubmitButton
              className="create-modal-btn"
              title="Retrieve data"
              disableWhenUnchangedFields
              onClick={handleSubmit(updateMemberInfoAsync)}
              dirtyFields={dirtyFields}
              defaultValues={defaultValues}
              maskedInputs={maskedInputs}
              values={watch()}
              loading={isSubmitting}
              disabled={isSubmitting || !isValid}
              dataTestId="mcm-go"
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default MemberCreateModal;
